import { Pill } from '@mantine/core';
import type { ReactNode } from 'react';
import { HelpContent, HelpContentHeading } from './HelpContent';

export function TrappedDefinition(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Public trapped</h4>
			</HelpContentHeading>

			<p>
				Someone is trapped or confined due to DTE's equipment and/or infrastructure such as wire down or pole
				down.
			</p>
		</HelpContent>
	);
}

export function ContactDefinition(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Contact with electrical equipment</h4>
				<Pill.Group>
					<Pill>Public contact</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>
				This category includes situations where individuals have received an electric shock, which has resulted
				in either an injury or a fatality. Such incidents are critical and require immediate attention due to
				the severe consequences involved. The reporting and handling of these cases follow established
				procedures to ensure proper response and documentation.
			</p>
		</HelpContent>
	);
}

export function DownWireDefinition(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Downed electrical wire</h4>
				<Pill.Group>
					<Pill>Down wire</Pill>
					<Pill>Wire down</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>
				A downed electrical wire refers to a situation where a wire that typically runs from pole to pole, or
				from a utility pole to a building, has fallen to the ground. These wires may be part of overhead power
				lines and can pose significant safety risks when they are no longer suspended as intended.
			</p>
			<p>
				The presence of a downed wire in the vicinity of a school, park, or playground presents a significant
				danger to children and other members of the community who frequent these areas.
			</p>
		</HelpContent>
	);
}

export function WireWithinReachDefinition(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Wire within reach</h4>
				<Pill.Group>
					<Pill>Low wire</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>
				This situation occurs when a wire running from one pole to another, or from a pole to a building, is
				hanging low enough that it can be touched by people. The accessibility of these wires increases the
				likelihood of accidental contact, which can result in electric shock or other hazards. It is important
				to identify and address any electrical wires that are not properly elevated to ensure the safety of the
				area.
			</p>
			<p>
				The presence of a low hanging wire in the vicinity of a school, park, or playground presents a
				significant danger to children and other members of the community who frequent these areas.
			</p>
		</HelpContent>
	);
}

export function ExposedUndergroundDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Underground lines exposed</h4>
			</HelpContentHeading>
			<p>
				Underground exposed lines refer to buried electrical lines that are no longer completely covered by soil
				or other materials. In these situations, the lines themselves become visible, or a portion of the line
				may be exposed above ground.
			</p>
			<p>
				<b>Common causes of exposure</b>
			</p>
			<ul>
				<li>Digging: Excavation activities can disturb the soil, uncovering previously buried lines.</li>
				<li>
					Erosion: Natural processes, such as water runoff, can wear away the covering material, revealing
					underground lines.
				</li>
				<li>
					Construction: Building projects or site development may result in the exposure of utility lines.
				</li>
			</ul>
		</HelpContent>
	);
}

export function MissingManholeDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Manhole cover missing</h4>
				<Pill.Group>
					<Pill>missing manhole</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>
				A missing manhole cover refers to the situation where the protective lid that is designed to cover an
				underground utility opening is missing.
			</p>
			<p>
				Normally, this cover serves as a barrier, preventing people and vehicles from accidentally falling into
				the open space below. The exposed opening created by a missing manhole cover poses a significant danger
				to anyone in the vicinity.
			</p>
		</HelpContent>
	);
}

export function ObjectOnWireDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Object on wire</h4>
				<Pill.Group>
					<Pill>Branches on wire</Pill>
					<Pill>Tree on wire</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>An object sitting on or touching a power line that does not belong there.</p>
			<p>This can include:</p>
			<ul>
				<li>
					Natural materials, such as:
					<ul>
						<li>
							<b>Branches</b>
						</li>
						<li>
							<b>Trees</b>
						</li>
					</ul>
				</li>
				<li>
					Man-made items like:
					<ul>
						<li>
							<b>Balloons</b>
						</li>
						<li>
							<b>Debris</b>
						</li>
					</ul>
				</li>
			</ul>
			<p>Risks:</p>
			<ul>
				<li>
					Damage to equipment
					<ul>
						<li>
							<b>Wires may be damaged</b>, sag, or break
						</li>
					</ul>
				</li>
				<li>
					Unsafe conditions
					<ul>
						<li>
							<b>Short circuits</b>
						</li>
						<li>
							<b>Fires</b>
						</li>
					</ul>
				</li>
				<li>
					Service interruptions
					<ul>
						<li>
							<b>Loss of power</b>
						</li>
						<li>
							<b>Flickering</b> lights
						</li>
					</ul>
				</li>
			</ul>
		</HelpContent>
	);
}

export function TrafficSignalOutDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Traffic signal out</h4>
			</HelpContentHeading>
			<p>
				A traffic signal out occurs when a stoplight loses power or malfunctions completely, resulting in all
				the lights being off. In this situation, none of the signal lights—red, yellow, or green—are
				illuminated, indicating that the signal is not operational.
			</p>
		</HelpContent>
	);
}

export function TransformerPoleNoiseDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Noise at pole</h4>
				<Pill.Group>
					<Pill>Transformer noise</Pill>
				</Pill.Group>
			</HelpContentHeading>

			<p>
				Transformer and pole-mounted electrical equipment generally produce a consistent, low-level hum during
				normal operation.
			</p>

			<p>Potential indicators that something inside equipment is loose or not functioning properly:</p>
			<ul>
				<li>
					<b>Sudden increases</b> in noise level
				</li>
				<li>
					<b>Sharp or unusual sounds</b>
				</li>
				<li>
					<b>Abrupt changes</b> in the usual operating noise
				</li>
			</ul>

			<p>
				These changes in sound can serve as early warning signs of issues that require inspection or
				maintenance.
			</p>
		</HelpContent>
	);
}

export function LowWireHazardDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Low wire</h4>
			</HelpContentHeading>
			<p>
				A low wire refers to an electrical line that is positioned lower than its standard height. While the
				wire is hanging lower than normal, it remains out of reach and does not pose an immediate contact
				hazard.
			</p>
		</HelpContent>
	);
}

export function FrayingWireDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Fraying wire</h4>
			</HelpContentHeading>
			<p>
				When a wire is described as "frayed," it means that the protective insulation surrounding the wire has
				been compromised or damaged. As a result, the metal conductor inside the wire may become exposed.
			</p>
		</HelpContent>
	);
}

export function BrokenGuyWireDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Broken guy wire</h4>
			</HelpContentHeading>
			<p>
				A guy wire is a strong, tensioned steel cable designed to provide essential support for utility poles.
				These cables are crucial in stabilizing the pole and preventing it from tipping over due to the combined
				effects of the weight of wires, wind, or mounted equipment.
			</p>
			<p>
				The primary function of a guy wire is to keep the utility pole upright and secure. If a guy wire snaps
				or comes loose, the supporting cable's absence can cause the pole to lean or become unstable. This
				compromised stability poses risks to the integrity of the utility infrastructure and safety in the
				surrounding area.
			</p>
		</HelpContent>
	);
}

export function ElectricityAllOutDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Power outage</h4>
				<Pill.Group>
					<Pill>Electricity is all out</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>
				A full power outage occurs when all sources of electricity in a home or building cease to function. This
				situation results in a total loss of electrical supply throughout the entire structure.
			</p>
			<p>
				During a complete power outage, every device or system dependent on electricity becomes inoperative.
				This includes all lighting fixtures, household appliances, heating systems such as furnaces, electrical
				outlets, internet equipment like Wi-Fi routers and modems, and mechanisms such as garage door openers.
			</p>
		</HelpContent>
	);
}

export function PartialPowerDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Partial power</h4>
				<Pill.Group>
					<Pill>One leg out</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>
				Partial power refers to a situation in which only specific circuits, outlets, or rooms within a home
				lose electrical power. Unlike a complete outage, the remainder of the household continues to function
				normally, with unaffected areas maintaining their usual electrical operation.
			</p>
			<p>Only certain circuits are impacted by the power loss.</p>
			<p>Some outlets or rooms may not have power, while others do.</p>
			<p>Unaffected sections of the home remain operational and continue to receive electricity.</p>
		</HelpContent>
	);
}

export function InterruptibleServiceDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Interruptible service</h4>
				<Pill.Group>
					<Pill>Interruptible air conditioning (IAC)</Pill>
					<Pill>Cool Currents</Pill>
					<Pill>Interruptible water heating (IWH)</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<div>
				<p>
					Residential and Commercial customers desiring separately metered interruptible service for central
					air conditioning and/or central heat pump use. DTE opts to interrupt customer equipment associated
					with this rate in a pattern of 15 minutes of service interruption followed by 15 minutes of the
					appliance operating.
				</p>
				<p>
					If only the IAC or IWH is without power, confirm that the meters are not interrupted by the Cool
					Currents process before an outage order is created.
				</p>
			</div>
		</HelpContent>
	);
}

export function BrightToDimLightsDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Low voltage</h4>
				<Pill.Group>
					<Pill>high voltage</Pill>
					<Pill>bright to dim lights</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>
				When customers notice the lights in their home becoming bright and then dim, this typically indicates
				that the voltage entering the home is not stable. Such voltage fluctuations can occur for several
				reasons.
			</p>
			<p>
				<b>Possible Reasons for Voltage Fluctuations:</b>
			</p>
			<ul>
				<li>
					<b>Loose Electrical Connections:</b> A loose connection within the electrical system can cause
					inconsistent voltage delivery, resulting in noticeable changes in the light brightness.
				</li>
				<li>
					<b>Neutral Wire Issues:</b> Problems with the neutral wire, which is responsible for carrying
					current back to the electrical panel, can also lead to voltage swings that affect the lighting.
				</li>
				<li>
					<b>Utility Line Problems:</b> Sometimes, the issue originates outside the home. If there is a fault
					or fluctuation on the utility lines, this can cause the voltage in the home to rise and fall, making
					the lights appear brighter or dimmer.
				</li>
			</ul>
		</HelpContent>
	);
}

export function FlickeringLightsDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Flickering lights</h4>
			</HelpContentHeading>
			<p>
				Flickering lights are a sign that electricity is not flowing smoothly through the electrical system.
				There are several common reasons why this may occur. Identifying the source of the problem can help
				address the issue efficiently and maintain a safe environment in the home or building.
			</p>
			<p>
				<b>Common Causes of Flickering Lights:</b>
			</p>
			<ul>
				<li>
					<b>Loose Bulb or Fixture:</b> If a light bulb or its fixture is not securely fastened, it may cause
					intermittent contact, resulting in flickering.
				</li>
				<li>
					<b>Bad Switch or Dimmer:</b> Faulty switches or dimmers can disrupt the flow of electricity, leading
					to inconsistent lighting performance.
				</li>
				<li>
					<b>Excessive Load on a Circuit:</b> When too many devices are connected to a single circuit, it can
					overload the system and cause the lights to flicker.
				</li>
				<li>
					<b>Loose Wiring:</b> Wiring that is not properly connected or has become loose over time can
					interrupt the steady flow of electricity, causing lights to flicker.
				</li>
				<li>
					<b>Voltage Fluctuations:</b> Changes in voltage levels can also make lights flicker, indicating a
					possible issue with the electrical supply.
				</li>
			</ul>
		</HelpContent>
	);
}

export function BrokenOrLeaningPoleDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Broken or leaning pole</h4>
				<Pill.Group>
					<Pill>Damaged pole</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>
				A broken or leaning pole indicates that the pole has suffered damage or is tilting beyond what is
				considered normal. Such conditions may compromise the pole's stability and pose potential safety
				hazards.
			</p>
		</HelpContent>
	);
}

export function PoleTransformerFireDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Pole or transformer fire</h4>
			</HelpContentHeading>
			<p>
				A transformer mounted on a utility pole that has caught on fire. This situation typically arises due to
				an internal failure within the equipment. When components inside the transformer malfunction or break
				down, it can result in overheating or sparks, ultimately leading to a fire. Recognizing the cause of
				such a fire is crucial for both safety and timely response.
			</p>
		</HelpContent>
	);
}

export function ArcingWireDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Arcing wire</h4>
				<Pill.Group>
					<Pill>sparking wire</Pill>
				</Pill.Group>
			</HelpContentHeading>
			<p>
				An arcing wire refers to a power line that is sparking or flashing. This happens when electricity jumps
				across a gap, which is a sign that something is wrong with the wire.
			</p>
			<p>
				Arcing typically occurs when the wire is damaged or when it comes into contact with something it should
				not touch. These situations create a gap that allows electricity to leap from one point to another,
				resulting in visible sparks or flashes.
			</p>
		</HelpContent>
	);
}

export function TreeInContactWithWireDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Tree in contact with wire</h4>
			</HelpContentHeading>
			<p>
				When a branch or an entire tree comes into contact with a power line, it creates a direct connection
				between the tree and the wire. This situation can result in several electrical issues.
			</p>
			<p>
				<b>Potential Consequences</b>
			</p>
			<ul>
				<li>
					<strong>Flickering lights:</strong> The contact may cause fluctuations in the electrical supply,
					leading to lights that flicker or dim unexpectedly.
				</li>
				<li>
					<strong>Sparks:</strong> In more severe cases, the tree touching the power line can generate sparks,
					which pose safety hazards.
				</li>
			</ul>
		</HelpContent>
	);
}

export function TreesGrowingIntoWiresDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Trees growing into wires</h4>
			</HelpContentHeading>
			<p>
				When trees grow near power lines, their branches can gradually extend toward the wires. As the branches
				get closer, there is an increased risk that they will come into direct contact with the electrical
				lines. This contact can result in electrical problems, such as flickering lights or even sparking. These
				issues occur because the branches interfere with the flow of electricity, potentially leading to
				dangerous situations or temporary power disruptions.
			</p>
		</HelpContent>
	);
}

export function DebrisLeftBehindByDTEDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Debris left behind by DTE</h4>
			</HelpContentHeading>
			<p>
				After the completion of work by the DTE crews, it was observed that the job site was not fully cleaned.
				Although the main project tasks were finished, various materials remained scattered throughout the area.
				These may include branches, dirt, wires, and scraps of equipment, which were not removed during the
				final cleanup process. The presence of such debris indicates that additional attention to site
				cleanliness may be required to ensure a fully restored environment.
			</p>
		</HelpContent>
	);
}

export function PoleLeftInDitchOrYardDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Pole left in ditch or yard</h4>
			</HelpContentHeading>
			<p>
				When DTE install new poles, the expectation is that the old poles will be removed promptly. However, in
				some cases, the old utility pole remains at the site even after its replacement. This situation is
				referred to as a "utility pole left behind," which means the previous pole was not taken down following
				the installation of the new one.
			</p>
		</HelpContent>
	);
}

export function GLNDescription(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Global Location Number (GLN)</h4>
			</HelpContentHeading>
			<p>A unique 12-digit number similar to GPS.</p>
			<ul>
				<li>The first 6 digits of a GLN correspond to a longitude</li>
				<li>The second 6 digits of a GLN correspond to a latitude</li>
			</ul>
		</HelpContent>
	);
}
